import { useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";
import MandatorySign from "../../Components/MandatorySign";
import { CiCircleAlert } from "react-icons/ci";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const NewCardChargeModal = ({
    account_id,
    show,
    handleClose,
    defaultCard,
    fetchData,
    fetchUserData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate()
    const stripe = useStripe();
    const elements = useElements();

    const [loadingSave, setLoadingSave] = useState(false);
    const [paymentId, setPaymentId] = useState(null)
    const [isAutomaticRechargeEnable, setIsAutomaticRechargeEnabled] = useState(false)
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [switchMethod, setSwitchMethod] = useState("card")
    const [computedColor, setComputedColor] = useState("")
    const [computedColorPlaceholder, setComputedColorPlaceholder] = useState("")
    const [formData, setFormData] = useState({
        creditAmount: parseFloat(5).toFixed(2),
        paymentMethodName: "",
        automaticTopUp: isAutomaticRechargeEnable,
        autoTopUpThreshold: parseFloat(5).toFixed(2),
        autoTopUpTarget: parseFloat(10).toFixed(2),
    });

    useEffect(() => {
        if (defaultCard && show) {
            console.log("Setting")
            setPaymentId(defaultCard.id)
            setIsAutomaticRechargeEnabled(defaultCard.auto_topup_enabled || false)
            setFormData({
                creditAmount: parseFloat(5).toFixed(2),
                paymentMethodName: defaultCard.payment_method_name,
                automaticTopUp: isAutomaticRechargeEnable,
                autoTopUpThreshold: (parseFloat(defaultCard.auto_topup_threshold_nano)).toFixed(2) || 5,
                autoTopUpTarget: (parseFloat(defaultCard.auto_topup_target_nano)).toFixed(2) || 10,
            });
        }
    }, [defaultCard, show, account_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        const numericFields = [
            "creditAmount",
            "autoTopUpThreshold",
            "autoTopUpTarget"
        ];

        if (!numericFields.includes(name)) {
            setFormData(prev => ({ ...prev, [name]: value }));
            return;
        }

        if (value === "") {
            setFormData(prev => ({ ...prev, [name]: "" }));
            return;
        }

        if (!/^\d*\.?\d*$/.test(value)) return;

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        const numericFields = [
            "creditAmount",
            "autoTopUpThreshold",
            "autoTopUpTarget"
        ];

        if (!numericFields.includes(name)) return;

        if (value === "") return;

        const number = parseFloat(value);

        if (!isNaN(number)) {
            setFormData(prev => ({
                ...prev,
                [name]: number.toFixed(2)
            }));
        }
    };

    const handleSave = async () => {
        if (!stripe || !elements) return;

        if (Number(formData.creditAmount) < 5) {
            setErrorMessage({ message: t.billing.min_5_credit, display: true });
            return;
        }

        if (Number(formData.autoTopUpThreshold) < 5) {
            setErrorMessage({ message: t.billing.min_5_threshold, display: true });
            return;
        }

        if (Number(formData.autoTopUpTarget) < 10) {
            setErrorMessage({ message: t.billing.min_10_threshold, display: true });
            return;
        }


        if (!formData.paymentMethodName) {
            setErrorMessage({ message: t.billing.pyment_method_name_required, display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {

            let paymentMethodId = null
            if (defaultCard) {
                paymentMethodId = defaultCard.payment_method_id
            } else {
                const cardElement = elements.getElement(CardElement);

                const { paymentMethod, error } = await stripe.createPaymentMethod({
                    type: "card",
                    card: cardElement,
                });

                if (error) {
                    throw new Error(error.message);
                }

                paymentMethodId = paymentMethod.id
            }

            const response = await fetch(`${GEN_BACKEND_LINK}/save_payment_recharge`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    payment_id: paymentId,
                    payment_method_id: paymentMethodId,
                    payment_method_name: formData.paymentMethodName,
                    auto_top_up: isAutomaticRechargeEnable,
                    credit_amount: formData.creditAmount,
                    auto_top_up_threshold: formData.autoTopUpThreshold,
                    auto_top_up_target: formData.autoTopUpTarget,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            closeModal();
            fetchData()
            fetchUserData()

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setErrorMessage({ message: "", display: false });
        setIsAutomaticRechargeEnabled(false)
        setLoadingSave(false);
        setFormData({
            creditAmount: parseFloat(5).toFixed(2),
            paymentMethodName: "",
            automaticTopUp: isAutomaticRechargeEnable,
            autoTopUpThreshold: parseFloat(5).toFixed(2),
            autoTopUpTarget: parseFloat(10).toFixed(2),
        });
    };

    useEffect(() => {
        const cc = getComputedStyle(document.documentElement)
            .getPropertyValue("--originalBlack")
            .trim();
        setComputedColor(cc)

        const ccp = getComputedStyle(document.documentElement)
            .getPropertyValue("--originalGray")
            .trim();
        setComputedColorPlaceholder(ccp)
    }, [defaultCard])

    useEffect(() => {
        if (!isAutomaticRechargeEnable) {
            setFormData(prev => ({
                ...prev,
                autoTopUpThreshold: parseFloat(5).toFixed(2),
                autoTopUpTarget: parseFloat(10).toFixed(2),
            }));
        }
    }, [isAutomaticRechargeEnable]);

    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">{t.billing.add_credits} <MandatorySign message={t.payments.sensitive_mex} icon="info" size="14px" direction="right" /></p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    <div className="label_input">
                        <label className="input_label">{t.billing.credits} <MandatorySign message={t.mandatory_field} /></label>
                        <input
                            type="text"
                            name="creditAmount"
                            value={formData.creditAmount}
                            onChange={handleChange}
                            placeholder="EX. $100"
                            onBlur={handleBlur}
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>
                    <div className="under_mex_cont">
                        <p className="role_warning_text">
                            Min $5.00
                        </p>
                    </div>

                    {switchMethod == "card" ? (

                        defaultCard ? (
                            <>
                                <div className="label_input">
                                    <label className="input_label">{t.billing.pay_method_name} <MandatorySign message={t.mandatory_field}/></label>
                                    <p className="register_form_first register_form_first_disabled fade-in">{defaultCard?.payment_method_name}</p>
                                </div>
                                <div className="label_input">
                                    <label className="input_label">
                                        {t.billing.card_details} {" "}
                                        {defaultCard?.brand && (
                                            <>
                                                of {defaultCard?.brand.toUpperCase() || "Null"}
                                            </>
                                        )}
                                    </label>
                                    <p className="register_form_first register_form_first_disabled fade-in">
                                        <span className="card_number_mock">
                                            xxxx xxxx xxxx xxxx {defaultCard?.last4}
                                        </span>
                                        <span className="card_number_expiry">
                                            {String(defaultCard?.exp_month).padStart(2, "0")}/
                                            {String(defaultCard?.exp_year).slice(-2).padStart(2, "0")}
                                        </span>
                                    </p>
                                </div>

                            </>
                        ) : (
                            <>
                                <div className="label_input">
                                    <label className="input_label">{t.billing.pay_method_name} <MandatorySign message={"Payment method name is required"} /></label>
                                    <input
                                        type="text"
                                        name="paymentMethodName"
                                        value={formData.paymentMethodName}
                                        onChange={handleChange}
                                        placeholder="Ex. MyCard"
                                        className="register_form_first fade-in"
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="label_input">
                                    <label className="input_label">
                                        {t.billing.card_details} <MandatorySign message={t.mandatory_field} />
                                    </label>

                                    <div className="card_det_container">
                                        <div className="card_det">
                                            <CardElement
                                                options={{
                                                    hidePostalCode: true,
                                                    style: {
                                                        base: {
                                                            fontSize: "14px",
                                                            color: computedColor,
                                                            "::placeholder": {
                                                                color: computedColorPlaceholder
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                </div>
                            </>
                        )
                    ) : (
                        <div className="sepa_cont">
                            <p className="role_warning_text">
                                For security and cost reasons, card payments are limited to $100 per top-up. Please use SEPA for larger amounts.
                            </p>
                            <div className="label_input">
                                <label className="input_label">
                                    IBAN <MandatorySign message={"IBAN is required"} />
                                </label>
                                <input
                                    type="text"
                                    name="paymentMethodName"
                                    // value={formData.paymentMethodName}
                                    onChange={handleChange}
                                    placeholder="Ex. IT0101..."
                                    className="register_form_first fade-in"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    )}


                </div>

                <div className="automatic_recharge_cont_quest">
                    <p className="automatic_recharge_cont_quest_title">{t.billing.automatic_topup}</p>
                    <label className="switch_container">
                        <input
                            id="web-search-toggle"
                            type="checkbox"
                            checked={isAutomaticRechargeEnable}
                            onChange={() => setIsAutomaticRechargeEnabled(!isAutomaticRechargeEnable)}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>
                {isAutomaticRechargeEnable && (
                    <div className="automatic_recharge_cont fade-in">
                        <div className="label_input">
                            <label className="input_label">{t.billing.when_below} <MandatorySign message={t.mandatory_field} /></label>
                            <input
                                type="text"
                                name="autoTopUpThreshold"
                                value={formData.autoTopUpThreshold}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Ex. $40"
                                className="register_form_first fade-in"
                                autoComplete="off"
                            />
                        </div>
                        <div className="under_mex_cont">
                            <p className="role_warning_text">
                                Min $5.00
                            </p>
                        </div>
                        <div className="label_input">
                            <label className="input_label">{t.billing.bring_up} <MandatorySign message={t.mandatory_field} /></label>
                            <input
                                type="text"
                                name="autoTopUpTarget"
                                value={formData.autoTopUpTarget}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Ex. $100"
                                className="register_form_first fade-in"
                                autoComplete="off"
                            />
                        </div>
                        <div className="under_mex_cont">
                            <p className="role_warning_text">
                                Min $10.00
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? <Spinner loading={true} /> : t.save}
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default NewCardChargeModal;